import { computed, onBeforeUnmount, ref, unref, watch } from 'vue'
import { message } from 'ant-design-vue'
import {
  getPatientPortraitDetail,
  getPatientPortraitDict,
  getPatientPortraitRefreshStatus,
  getPatientPortraitRefreshTask,
  readPatientPortrait,
  refreshPatientPortrait
} from '@/api/patientFile'

// 刷新任务默认倒计时秒数与轮询间隔
const DEFAULT_REMAINING_SECONDS = 60
const REFRESH_POLL_INTERVAL = 5000
// 轮询连续失败上限：达到后终止轮询，避免状态接口持续异常时无休止请求
const MAX_POLL_FAILURES = 3

// 刷新任务状态
const REFRESH_STATUS = {
  PROCESSING: 'PROCESSING',
  SUCCEEDED: 'SUCCEEDED',
  SUCCEEDED_NO_CHANGE: 'SUCCEEDED_NO_CHANGE',
  FAILED: 'FAILED',
  NOT_STARTED: 'NOT_STARTED'
}

// 用接口字段原样兜底，避免视图层出现大量空值判断。
function createDefaultData(ids = {}) {
  const { ucPatientId } = ids || {}
  return {
    ucDoctorId: ids.ucDoctorId || '',
    ucPatientId: ucPatientId || '--',
    nameDisplay: '患者',
    age: '--',
    gender: '--',
    genderLabel: '--',
    diagnosisDefaultTags: [],
    diagnosisTags: [],
    diagnosisManualTags: '',
    idCard: '--',
    phone: '--',
    firstVisitDate: '--',
    communicationCount: '--',
    canEdit: false,
    headUrl: '',
    completionPercent: 0,
    occupation: '',
    occupationLabel: '',
    maritalStatus: '',
    maritalStatusLabel: '',
    currentStageValue: '--',
    currentStageLabel: '--',
    followUpCountValue: '--',
    followUpCountLabel: '--',
    currentStatus: '--',
    currentStatusContext: '',
    keyRiskLabel: '--',
    todoItems: '--',
    suggestedAction: '--',
    currentStageSource: '--',
    timeline: [],
    currentDiseasePhaseValue: '--',
    currentDiseasePhaseLabel: '--',
    currentEngagementStageValue: '--',
    currentEngagementStageLabel: '--',
    currentCourseMedicationDaysLabel: '--',
    currentCourseMedicationDaysValue: '--',
    communicationComplianceValue: '--',
    communicationComplianceLabel: '--',
    communicationComplianceReason: '--',
    contactPreferenceValue: '--',
    contactPreferenceLabel: '--',
    contactPreferenceReason: '--',
    responsePatternValue: '--',
    responsePatternLabel: '--',
    responsePatternReason: '--',
    emotionStateValue: '--',
    emotionStateLabel: '--',
    emotionStateReason: '--',
    diseaseDiagnosisPrimary: '',
    diseaseDiagnosisPatientSelfReport: '',
    medicationEndDateValue: '--',
    medicationEndDateDaysRemaining: null,
    currentMedication: [],
    historicalMedications: [],
    patientPortraitBackground: '',
    selfReportedFeedbacks: [],
    complaintTendency: null
  }
}

async function requestPortraitDetail(params) {
  return getPatientPortraitDetail(params)
}

async function requestPortraitDict() {
  return getPatientPortraitDict()
}

function createRequestParams({ ucDoctorId, ucPatientId }) {
  return {
    ucDoctorId: unref(ucDoctorId) || '',
    ucPatientId: unref(ucPatientId) || ''
  }
}

function hasRequiredIds(params) {
  return Boolean(params.ucDoctorId && params.ucPatientId)
}

// 详情返回 newFieldCodes 有值时上报已读
// 静默失败不阻塞展示，下次打开若仍返回标记会自然重试
function markPortraitReadIfNeeded(params, detail) {
  const newFieldCodes = detail?.newFieldCodes
  if (!Array.isArray(newFieldCodes) || !newFieldCodes.length) return
  readPatientPortrait({
    ucDoctorId: params.ucDoctorId,
    ucPatientId: params.ucPatientId
  }).catch(() => {})
}

export function usePortraitData({ visible, ucDoctorId, ucPatientId, fetcher }) {
  const loading = ref(false)
  const initialized = ref(false)
  const portraitDict = ref([])
  const portraitData = ref(
    createDefaultData({
      ucDoctorId: unref(ucDoctorId),
      ucPatientId: unref(ucPatientId)
    })
  )

  const hasData = computed(() => Boolean(portraitData.value))

  const refreshing = ref(false)
  const remainSeconds = ref(0)

  let pollTimer = null
  // 轮询连续失败计数，防止状态接口持续异常时无限轮询
  let pollFailCount = 0

  const clearRefreshTimers = () => {
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  const stopRefresh = () => {
    clearRefreshTimers()
    refreshing.value = false
  }

  // 刷新完成：停止计时并回源拉取最新画像数据
  const finishRefresh = async () => {
    clearRefreshTimers()
    await load()
    refreshing.value = false
  }

  // 处理轮询到的任务状态，返回 true 表示任务已终结、停止轮询
  const handleRefreshStatus = async res => {
    const status = res?.status
    if (status === REFRESH_STATUS.SUCCEEDED) {
      // 刷新成功：提示后停止计时并回源拉取最新画像数据；
      // profileUpdatedAt 无值说明是首次生成，提示文案切换为“生成成功”
      message.success(portraitData.value?.profileUpdatedAt ? '刷新成功' : '生成成功')
      await finishRefresh()
      return true
    }
    if (status === REFRESH_STATUS.SUCCEEDED_NO_CHANGE) {
      // 数据无变化：任务正常结束，重新拉取页面数据
      message.success('当前已是最新画像')
      await finishRefresh()
      return true
    }
    if (status === REFRESH_STATUS.FAILED || status === REFRESH_STATUS.NOT_STARTED) {
      // 任务失败或未开始：终止轮询并提示更新失败
      stopRefresh()
      message.error(res.errorMessage || '更新失败')
      return true
    }
    // PROCESSING：继续轮询
    return false
  }

  // 递归 setTimeout 保证上一次状态请求返回后才调度下一次，避免请求堆积
  const pollRefreshStatus = taskId => {
    pollTimer = setTimeout(async () => {
      // 计时器可能已被清理（弹窗关闭/刷新完成），直接终止轮询
      if (!refreshing.value) return
      try {
        const res = await getPatientPortraitRefreshStatus({
          ...createRequestParams({ ucDoctorId, ucPatientId }),
          taskId
        })
        if (!refreshing.value) return
        pollFailCount = 0
        if (await handleRefreshStatus(res)) return
      } catch (error) {
        // 单次轮询失败不中断流程；连续失败达到上限时终止并单次提示
        pollFailCount += 1
        if (pollFailCount >= MAX_POLL_FAILURES) {
          stopRefresh()
          message.error('更新失败，请稍后重试')
          return
        }
      }
      pollRefreshStatus(taskId)
    }, REFRESH_POLL_INTERVAL)
  }

  const startRefreshPolling = taskId => {
    clearRefreshTimers()
    pollFailCount = 0
    refreshing.value = true
    pollRefreshStatus(taskId)
  }

  // 手动刷新：提交任务成功后进入“倒计时 + 轮询”流程
  const refresh = async () => {
    if (refreshing.value) return
    refreshing.value = true
    // 提交期间先展示默认预估时长，避免响应返回前文案闪现“预计需要0秒”
    remainSeconds.value = DEFAULT_REMAINING_SECONDS

    try {
      const res = await refreshPatientPortrait(createRequestParams({ ucDoctorId, ucPatientId }))

      // 提交期间弹窗已关闭：放弃后续轮询，下次打开时由恢复任务逻辑接管
      if (!unref(visible)) {
        refreshing.value = false
        return
      }

      // code 4008：后端将“已是最新画像”作为业务异常返回，此处按成功语义提示
      if (res?.code === '4008') {
        refreshing.value = false
        message.success(res.msg)
        return
      }

      if (res?.taskId) {
        remainSeconds.value = res.estimatedSeconds || DEFAULT_REMAINING_SECONDS
        startRefreshPolling(res.taskId)
      } else {
        console.error('刷新患者画像失败，taskId不存在')
        refreshing.value = false
        message.error('更新失败，请稍后重试')
      }
    } catch (error) {
      refreshing.value = false
      console.error('刷新患者画像失败', error)
    }
  }

  // 弹窗打开时检查是否有进行中的刷新任务，有则恢复轮询
  const resumeProcessingTask = async () => {
    if (refreshing.value) return
    try {
      const res = await getPatientPortraitRefreshTask(
        createRequestParams({ ucDoctorId, ucPatientId })
      )
      // 恢复请求期间弹窗已关闭：放弃轮询，避免关闭状态下持续后台请求
      if (!unref(visible)) return
      if (res?.taskId) {
        remainSeconds.value = res.estimatedSeconds || DEFAULT_REMAINING_SECONDS
        startRefreshPolling(res.taskId)
      }
    } catch (error) {
      // 恢复失败不影响弹窗正常展示
    }
  }

  const load = async () => {
    // 请求参数始终来自组件入参，避免联调 mock ID 污染真实患者上下文。
    const params = createRequestParams({ ucDoctorId, ucPatientId })

    // 缺少医患 ID 时不发起详情请求，保留默认态供页面安全渲染。
    if (!hasRequiredIds(params)) {
      portraitData.value = createDefaultData(params)
      initialized.value = true
      return
    }

    loading.value = true
    try {
      const requestFn =
        typeof unref(fetcher) === 'function' ? unref(fetcher) : requestPortraitDetail
      // 详情和字典互不依赖，失败时分别回退，避免单个接口拖垮整个弹窗。
      const [detailResult, dictResult] = await Promise.allSettled([
        requestFn(params),
        requestPortraitDict()
      ])

      if (detailResult.status === 'fulfilled') {
        portraitData.value = {
          ...createDefaultData(params),
          ...(detailResult.value || {})
        }
        markPortraitReadIfNeeded(params, detailResult.value)
      } else {
        portraitData.value = createDefaultData(params)
      }

      if (dictResult.status === 'fulfilled') {
        portraitDict.value = Array.isArray(dictResult.value) ? dictResult.value : []
      }
    } catch (error) {
      portraitData.value = createDefaultData(params)
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  watch(
    () => [unref(visible), unref(ucDoctorId), unref(ucPatientId)],
    (
      [currentVisible, currentUcDoctorId, currentUcPatientId],
      [lastVisible, lastUcDoctorId, lastUcPatientId] = []
    ) => {
      if (!currentVisible) return
      // 请求上下文变化时终止旧任务的轮询，避免旧患者任务污染新上下文
      if (
        lastUcDoctorId !== undefined &&
        (currentUcDoctorId !== lastUcDoctorId || currentUcPatientId !== lastUcPatientId)
      ) {
        stopRefresh()
      }
      // 只在首次打开或请求上下文变化时重新拉取，避免重复请求。
      if (
        !initialized.value ||
        currentUcDoctorId !== lastUcDoctorId ||
        currentUcPatientId !== lastUcPatientId ||
        !lastVisible
      ) {
        load()
      }
      // 弹窗每次打开时恢复进行中的刷新任务轮询
      if (!lastVisible) {
        resumeProcessingTask()
      }
    },
    { immediate: true }
  )

  // 弹窗关闭时终止轮询与倒计时，组件销毁时兜底清理
  watch(
    () => unref(visible),
    currentVisible => {
      if (!currentVisible) stopRefresh()
    }
  )

  onBeforeUnmount(stopRefresh)

  return {
    loading,
    initialized,
    hasData,
    portraitDict,
    portraitData,
    refreshing,
    remainSeconds,
    refresh,
    resumeProcessingTask,
    reload: load
  }
}
